/**
 * RateLimiter Service
 * Implements rate limiting for authentication attempts
 */

class RateLimiter {
  constructor() {
    this.attempts = new Map(); // Store attempts by key (email or IP)
    this.config = {
      maxAttempts: 5, // Maximum attempts allowed
      timeWindow: 15 * 60 * 1000, // 15 minutes in milliseconds
      lockoutDuration: 30 * 60 * 1000, // 30 minutes lockout
      ipMaxAttempts: 50, // Maximum attempts from same IP
      ipTimeWindow: 60 * 60 * 1000, // 1 hour for IP-based limiting
    };
  }

  /**
   * Check if an attempt is allowed
   * @param {string} email - User email
   * @param {string} ipAddress - IP address
   * @returns {Object} Rate limit result
   */
  checkRateLimit(email, ipAddress) {
    const now = Date.now();
    
    // Check email-based rate limiting
    const emailResult = this.checkEmailRateLimit(email, now);
    if (!emailResult.allowed) {
      return emailResult;
    }

    // Check IP-based rate limiting
    const ipResult = this.checkIPRateLimit(ipAddress, now);
    if (!ipResult.allowed) {
      return ipResult;
    }

    return {
      allowed: true,
      remainingAttempts: emailResult.remainingAttempts,
      resetTime: emailResult.resetTime
    };
  }

  /**
   * Record a failed attempt
   * @param {string} email - User email
   * @param {string} ipAddress - IP address
   */
  recordFailedAttempt(email, ipAddress) {
    const now = Date.now();
    
    // Record email-based attempt
    this.recordEmailAttempt(email, now, false);
    
    // Record IP-based attempt
    this.recordIPAttempt(ipAddress, now, false);
  }

  /**
   * Record a successful attempt (reset counters)
   * @param {string} email - User email
   * @param {string} ipAddress - IP address
   */
  recordSuccessfulAttempt(email, ipAddress) {
    const now = Date.now();
    
    // Reset email-based attempts
    this.recordEmailAttempt(email, now, true);
    
    // Reset IP-based attempts
    this.recordIPAttempt(ipAddress, now, true);
  }

  /**
   * Check email-based rate limiting
   * @param {string} email - User email
   * @param {number} now - Current timestamp
   * @returns {Object} Rate limit result
   */
  checkEmailRateLimit(email, now) {
    const key = `email:${email}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };

    // Clean old attempts outside time window
    const cutoffTime = now - this.config.timeWindow;
    attempts.attempts = attempts.attempts.filter(timestamp => timestamp > cutoffTime);

    // Check if locked out
    if (attempts.lockedUntil && now < attempts.lockedUntil) {
      return {
        allowed: false,
        reason: 'ACCOUNT_LOCKED',
        message: `Account locked until ${new Date(attempts.lockedUntil).toLocaleString()}`,
        lockoutTime: attempts.lockedUntil,
        remainingAttempts: 0
      };
    }

    // Check if too many attempts
    if (attempts.attempts.length >= this.config.maxAttempts) {
      // Lock the account
      attempts.lockedUntil = now + this.config.lockoutDuration;
      this.attempts.set(key, attempts);

      return {
        allowed: false,
        reason: 'TOO_MANY_ATTEMPTS',
        message: `Too many failed attempts. Account locked for ${this.config.lockoutDuration / 60000} minutes.`,
        lockoutTime: attempts.lockedUntil,
        remainingAttempts: 0
      };
    }

    return {
      allowed: true,
      remainingAttempts: this.config.maxAttempts - attempts.attempts.length,
      resetTime: attempts.attempts.length > 0 ? attempts.attempts[0] + this.config.timeWindow : now
    };
  }

  /**
   * Check IP-based rate limiting
   * @param {string} ipAddress - IP address
   * @param {number} now - Current timestamp
   * @returns {Object} Rate limit result
   */
  checkIPRateLimit(ipAddress, now) {
    const key = `ip:${ipAddress}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };

    // Clean old attempts outside time window
    const cutoffTime = now - this.config.ipTimeWindow;
    attempts.attempts = attempts.attempts.filter(timestamp => timestamp > cutoffTime);

    // Check if too many attempts from this IP
    if (attempts.attempts.length >= this.config.ipMaxAttempts) {
      return {
        allowed: false,
        reason: 'IP_BLOCKED',
        message: `Too many attempts from this IP address. Please try again later.`,
        remainingAttempts: 0,
        resetTime: attempts.attempts[0] + this.config.ipTimeWindow
      };
    }

    return {
      allowed: true,
      remainingAttempts: this.config.ipMaxAttempts - attempts.attempts.length,
      resetTime: attempts.attempts.length > 0 ? attempts.attempts[0] + this.config.ipTimeWindow : now
    };
  }

  /**
   * Record an attempt for email
   * @param {string} email - User email
   * @param {number} now - Current timestamp
   * @param {boolean} success - Whether attempt was successful
   */
  recordEmailAttempt(email, now, success) {
    const key = `email:${email}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };

    if (success) {
      // Reset on successful attempt
      attempts.attempts = [];
      attempts.lockedUntil = null;
    } else {
      // Add failed attempt
      attempts.attempts.push(now);
      attempts.lastAttempt = now;
    }

    this.attempts.set(key, attempts);
  }

  /**
   * Record an attempt for IP
   * @param {string} ipAddress - IP address
   * @param {number} now - Current timestamp
   * @param {boolean} success - Whether attempt was successful
   */
  recordIPAttempt(ipAddress, now, success) {
    const key = `ip:${ipAddress}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };

    if (success) {
      // Reset on successful attempt
      attempts.attempts = [];
    } else {
      // Add failed attempt
      attempts.attempts.push(now);
      attempts.lastAttempt = now;
    }

    this.attempts.set(key, attempts);
  }

  /**
   * Get rate limit status for an email
   * @param {string} email - User email
   * @returns {Object} Rate limit status
   */
  getEmailStatus(email) {
    const key = `email:${email}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };
    const now = Date.now();

    // Clean old attempts
    const cutoffTime = now - this.config.timeWindow;
    attempts.attempts = attempts.attempts.filter(timestamp => timestamp > cutoffTime);

    return {
      attempts: attempts.attempts.length,
      maxAttempts: this.config.maxAttempts,
      remainingAttempts: Math.max(0, this.config.maxAttempts - attempts.attempts.length),
      isLocked: attempts.lockedUntil && now < attempts.lockedUntil,
      lockoutTime: attempts.lockedUntil,
      resetTime: attempts.attempts.length > 0 ? attempts.attempts[0] + this.config.timeWindow : now
    };
  }

  /**
   * Get rate limit status for an IP
   * @param {string} ipAddress - IP address
   * @returns {Object} Rate limit status
   */
  getIPStatus(ipAddress) {
    const key = `ip:${ipAddress}`;
    const attempts = this.attempts.get(key) || { attempts: [], lastAttempt: 0 };
    const now = Date.now();

    // Clean old attempts
    const cutoffTime = now - this.config.ipTimeWindow;
    attempts.attempts = attempts.attempts.filter(timestamp => timestamp > cutoffTime);

    return {
      attempts: attempts.attempts.length,
      maxAttempts: this.config.ipMaxAttempts,
      remainingAttempts: Math.max(0, this.config.ipMaxAttempts - attempts.attempts.length),
      resetTime: attempts.attempts.length > 0 ? attempts.attempts[0] + this.config.ipTimeWindow : now
    };
  }

  /**
   * Clear rate limit data (for testing or admin purposes)
   * @param {string} email - Optional email to clear
   * @param {string} ipAddress - Optional IP to clear
   */
  clearRateLimit(email = null, ipAddress = null) {
    if (email) {
      this.attempts.delete(`email:${email}`);
    }
    if (ipAddress) {
      this.attempts.delete(`ip:${ipAddress}`);
    }
    if (!email && !ipAddress) {
      this.attempts.clear();
    }
  }

  /**
   * Update rate limit configuration
   * @param {Object} newConfig - New configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create singleton instance
const rateLimiter = new RateLimiter();

export default rateLimiter;
